<script setup lang="ts">
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Method } from 'axios'

import { useAuthStore } from '@/stores/auth-store'
import useForm from '@/hooks/use-form'
import { ResourceResponse } from '@/types/general'
import { Port } from '@/types/cargo-loading'

import MyInput from '@/components/my-components/form/MyInput.vue'
import MyModal from '@/components/my-components/MyModal.vue'
import MyForm from '@/components/my-components/form/MyForm.vue'
import MyButton from '@/components/my-components/MyButton.vue'

interface Props {
    modelValue: boolean
    port?: Port | null
}

interface Form {
    shortName: string
    name: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
    (e: 'close'): void
    (e: 'saved'): void
}>()

const authStore = useAuthStore()
const { t } = useI18n()
const { data, submit, errors, reset, loading } = useForm<Form>({
    shortName: '',
    name: '',
})

const title = computed(() => {
    if (props.port?.id) {
        return t('updateEntity', { entity: t('port') })
    } else {
        return t('createEntity', { entity: t('port') })
    }
})

async function onSubmit() {
    const method: Method = props.port?.id ? 'PUT' : 'POST'
    const createRoute = window.route('cl.company.ports.store', {
        company: authStore.companyId,
    })
    const updateRoute = window.route('cl.company.ports.update', {
        company: authStore.companyId,
        port: props.port?.id ?? '',
    })

    const response = await submit<ResourceResponse<Port>>(
        method,
        props.port?.id ? updateRoute : createRoute,
    )

    if (response !== undefined) {
        emit('close')
        emit('saved')
    }
}

watch(
    () => props.modelValue,
    () => {
        if (!props.modelValue) return

        reset({
            shortName: props.port?.shortName ?? '',
            name: props.port?.name ?? '',
        })
    },
    { immediate: true },
)
</script>

<template>
    <MyModal :value="props.modelValue" @close="emit('close')">
        <template #title>
            {{ title }}
        </template>
        <MyForm :errors="errors" @submit.prevent="onSubmit">
            <div class="space-y-2">
                <div class="flex space-x-3">
                    <MyInput
                        v-model="data.shortName"
                        name="shortName"
                        type="text"
                        placeholder="shortName"
                        :label="t('shortName')"
                        autofocus
                    />

                    <MyInput
                        v-model="data.name"
                        name="name"
                        type="text"
                        placeholder="name"
                        :label="t('name')"
                    />
                </div>
            </div>
            <div class="mt-4 flex justify-end gap-6">
                <MyButton
                    :disabled="loading"
                    scheme="primary"
                    v-text="props.port?.id ? t('update') : t('create')"
                />
            </div>
        </MyForm>
    </MyModal>
</template>
